import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faLocationDot,
    faPhone
} from '@fortawesome/free-solid-svg-icons';

import { localizePath } from '../../utils/language';
import './Contacts.css';

const PRIMARY_PHONE_DISPLAY = '+998 77 255 44 40';
const SECONDARY_PHONE_DISPLAY = '+998 77 266 44 40';
const PRIMARY_PHONE_RAW = '998772554440';
const SECONDARY_PHONE_RAW = '998772664440';
const TELEGRAM_USERNAME = '@podiatechuz';
const TELEGRAM_URL = 'https://t.me/podiatechuz';
const INSTAGRAM_URL = 'https://www.instagram.com/podiatech.uz?igsh=N2hyeDZvdHAyeHl6';
const FACEBOOK_URL = 'https://www.facebook.com/share/17NBkys7qi/?mibextid=wwXIfr';
const EMAIL = 'podiatechuz@gmail.com';

const TELEGRAM_ICON = (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.223-.535.223l.19-2.712 4.94-4.465c.215-.19-.047-.295-.335-.105l-6.107 3.843-2.63-.823c-.572-.18-.582-.572.12-.847l10.27-3.96c.477-.172.896.116.587.874z" />
    </svg>
);

const INSTAGRAM_ICON = (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
);

const FACEBOOK_ICON = (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.019 4.388 11.009 10.125 11.927v-8.437H7.078v-3.49h3.047V9.41c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.082 24 18.092 24 12.073z" />
    </svg>
);

// Статические страницы филиалов (public/filiallar/*) — отдельный HTML вне SPA
const BRANCH_PAGE_SLUGS = {
    branch_yashnobod: 'toshkent-yashnobod',
    branch_mirzo_ulugbek: 'toshkent-mirzo-ulugbek',
    branch_samarkand: 'samarqand',
    branch_andijan: 'andijon'
};

const MAP_EMBED_SOURCES = {
    branch_yashnobod:
        'https://maps.google.com/maps?q=41.306276,69.293580&t=m&z=16&output=embed',

    branch_mirzo_ulugbek:
        'https://maps.google.com/maps?q=41.339393,69.343527&t=m&z=16&output=embed',

    branch_samarkand:
        'https://maps.google.com/maps?q=39.678778,66.916728&t=m&z=16&output=embed',

    branch_andijan:
        'https://maps.google.com/maps?q=40.778582,72.323335&t=m&z=16&output=embed'
};

const Contacts = () => {
    const { t, i18n } = useTranslation();
    const [selectedRegion, setSelectedRegion] = useState('tashkent_city');
    const [activeLocationId, setActiveLocationId] = useState('branch_yashnobod');

    const contactItems = [
    {
        key: 'phone',
        icon: faPhone,
        color: 'primary',
        phoneNumbers: [
            { display: PRIMARY_PHONE_DISPLAY, raw: PRIMARY_PHONE_RAW },
            { display: SECONDARY_PHONE_DISPLAY, raw: SECONDARY_PHONE_RAW }
        ]
    },
    {
        key: 'telegram',
        iconNode: TELEGRAM_ICON,
        color: 'info',
        value: '@podiatechuz',
        href: TELEGRAM_URL
    },
    {
        key: 'instagram',
        iconNode: INSTAGRAM_ICON,
        color: 'instagram',
        value: 'podiatech.uz',
        href: INSTAGRAM_URL
    },
    {
        key: 'facebook',
        iconNode: FACEBOOK_ICON,
        color: 'primary',
        value: 'Podiatech',
        href: FACEBOOK_URL
    }
    // {
    //     key: 'email',
    //     icon: faEnvelope,
    //     color: 'accent',
    //     value: EMAIL,
    //     href: `mailto:${EMAIL}`
    // }
];

    const regionOptions = useMemo(
        () => [
            { id: 'tashkent_city', name: t('contacts_info.regions.tashkent_city') },
            { id: 'samarkand', name: t('contacts_info.regions.samarkand') },
            { id: 'andijan', name: t('contacts_info.regions.andijan') }
        ],
        [i18n.language, t]
    );

    const branchesByRegion = useMemo(
        () => ({
            tashkent_city: [
                {
                    id: 'branch_yashnobod',
                    name: t('contacts_info.branch1_name'),
                    address: t('contacts_info.branch1_address'),
                    query: t('contacts_info.branch1_map_query')
                },
                {
                    id: 'branch_mirzo_ulugbek',
                    name: t('contacts_info.branch2_name'),
                    address: t('contacts_info.branch2_address'),
                    query: t('contacts_info.branch2_map_query')
                },
            ],
            samarkand: [
                {
                    id: 'branch_samarkand',
                    name: t('contacts_info.branch3_name'),
                    address: t('contacts_info.branch3_address'),
                    query: t('contacts_info.branch3_map_query')
                }
            ],
            andijan: [
                {
                    id: 'branch_andijan',
                    name: t('contacts_info.branch4_name'),
                    address: t('contacts_info.branch4_address'),
                    query: t('contacts_info.branch4_map_query')
                }
            ]
        }),
        [i18n.language, t]
    );

    const branches = branchesByRegion[selectedRegion] || [];
    const activeLocation = branches.find((location) => location.id === activeLocationId) || branches[0];
    const mapQuery = activeLocation?.query || activeLocation?.address || '';
    const mapSrc = MAP_EMBED_SOURCES[activeLocation?.id]
        || `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
    const handleRegionChange = (event) => {
        const nextRegion = event.target.value;
        const nextBranches = branchesByRegion[nextRegion] || [];
        setSelectedRegion(nextRegion);
        setActiveLocationId(nextBranches[0]?.id || '');
    };

    return (
        <section id="contacts" className="section contacts-section">
            <div className="container">
                {/* Header */}
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="section-title">{t('contacts_info.title')}</h2>
                    <p className="section-subtitle">{t('contacts_info.subtitle')}</p>
                </motion.div>

                <div className="contacts-content">
                    {/* Contact Cards */}
                    <motion.div
                        className="contacts-grid"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        {contactItems.map((item) => {
                            const content = (
                                <>
                                    <span className={`contact-card-icon contact-card-icon-${item.color}`} aria-hidden="true">
                                        {item.iconNode || <FontAwesomeIcon icon={item.icon} />}
                                    </span>
                                    <div className="contact-card-content">
                                        <span className="contact-card-label">{t(`contacts_info.${item.key}`)}</span>
                                        <span className={`contact-card-value${item.phoneNumbers ? ' contact-card-value-multi' : ''}`}>
                                            {item.phoneNumbers
                                                ? item.phoneNumbers.map((phone, index) => (
                                                    <span key={phone.raw} className="contact-card-phone-line">
                                                        <a href={`tel:+${phone.raw}`}>{phone.display}</a>
                                                        {index < item.phoneNumbers.length - 1 ? <br /> : null}
                                                    </span>
                                                ))
                                                : item.value}
                                        </span>
                                    </div>
                                </>
                            );

                            return item.href ? (
                            <a
                                key={item.key}
                                href={item.href}
                                target={item.href.startsWith('http') ? '_blank' : undefined}
                                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                className={`contact-card contact-card-${item.color}`}
                            >
                                {content}
                            </a>
                            ) : (
                            <div
                                key={item.key}
                                className={`contact-card contact-card-${item.color}`}
                            >
                                {content}
                            </div>
                            );
                        })}
                    </motion.div>

                    {/* Address & Hours */}
                    <motion.div
                        className="address-section"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <div className="address-card">
                            <div className="address-item address-item-primary">
                                <span className="address-icon address-icon-primary" aria-hidden="true">
                                    <FontAwesomeIcon icon={faLocationDot} />
                                </span>
                                <div>
                                    <span className="address-label">{t('contacts_info.address')}</span>
                                    {/* <span className="address-value">
                                        {activeLocation?.address || t('contacts_info.address_value')}
                                    </span> */}
                                    <span className="address-value">{t('contacts_info.address_value')}</span>
                                </div>
                            </div>
                            <div className="address-item address-item-secondary">
                                <span className="address-icon address-icon-secondary" aria-hidden="true">
                                    <FontAwesomeIcon icon={faClock} />
                                </span>
                                <div>
                                    <span className="address-label">{t('contacts_info.working_hours')}</span>
                                    <span className="address-value">{t('contacts_info.working_hours_value')}</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Map */}
                    <motion.div
                        className="map-container"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <div className="map-section">
                            <div className="map-sidebar">
                                <div className="map-card map-region-select">
                                    <div className="map-card-header">
                                        <span className="map-card-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={faLocationDot} />
                                        </span>
                                        <div>
                                            <span className="map-card-title">{t('contacts_info.region_select_title')}</span>
                                            <span className="map-card-subtitle">{t('contacts_info.branches_subtitle')}</span>
                                        </div>
                                    </div>
                                    <select
                                        id="map-region-select"
                                        className="map-region-select-field"
                                        value={selectedRegion}
                                        onChange={handleRegionChange}
                                        aria-label={t('contacts_info.region_select_title')}
                                    >
                                        {regionOptions.map((region) => (
                                            <option key={region.id} value={region.id}>
                                                {region.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="map-branches">
                                    <div className="map-branches-header">
                                        <span className="map-branches-title">{t('contacts_info.branches_title')}</span>
                                        <span className="map-branches-subtitle">
                                            {t('contacts_info.branches_subtitle')}
                                        </span>
                                    </div>
                                    <div className="map-branches-list">
                                        {branches.map((branch) => (
                                            <button
                                                key={branch.id}
                                                type="button"
                                                className={`branch-button ${
                                                    activeLocationId === branch.id ? 'active' : ''
                                                }`}
                                                onClick={() => setActiveLocationId(branch.id)}
                                            >
                                                <span className="branch-name">{branch.name}</span>
                                                <span className="branch-address">{branch.address}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {BRANCH_PAGE_SLUGS[activeLocation?.id] && (
                                        <a
                                            className="branch-page-link"
                                            href={localizePath(
                                                i18n.language,
                                                `/filiallar/${BRANCH_PAGE_SLUGS[activeLocation.id]}`
                                            )}
                                        >
                                            {t('contacts_info.branch_page_link')} →
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="map-frame">
                                <iframe
                                    title={activeLocation?.name || t('contacts_info.map_title')}
                                    src={mapSrc}
                                    className="map-iframe"
                                    loading="lazy"
                                    fetchpriority="low"
                                    allowFullScreen
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contacts;
